// src/components/Dashboard/charts/StatusDistributionCharts.tsx
import React from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";

interface StatusDistributionChartsProps {
  foldersData: any[];
  requirementsData: any[];
}

const StatusDistributionCharts: React.FC<StatusDistributionChartsProps> = ({
  foldersData,
  requirementsData,
}) => {
  if (!foldersData || !requirementsData) return null;

  // Format folder status data
  const formattedFolderData = foldersData.map((item) => ({
    name: item.status === "completed" ? "Completados" : "En Progreso",
    value: item.count,
  }));

  // Format requirement status data
  const formattedRequirementData = requirementsData.map((item) => {
    let name = "Unknown";
    switch (item.status) {
      case "pending_upload":
        name = "Pendientes de Carga";
        break;
      case "pending_review":
        name = "Pendientes de Revisión";
        break;
      case "invalid":
        name = "Inválidos";
        break;
      case "valid":
        name = "Válidos";
        break;
    }
    return {
      name,
      value: item.count,
    };
  });

  // Colors for pie charts
  const FOLDER_COLORS = ["#10b981", "#f59e0b"]; // green, amber
  const REQUIREMENT_COLORS = ["#ef4444", "#f59e0b", "#3b82f6", "#10b981"]; // blue, amber, red, green

  const RADIAN = Math.PI / 180;
  const renderCustomizedLabel = ({
    cx,
    cy,
    midAngle,
    innerRadius,
    outerRadius,
    percent,
  }: any) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? "start" : "end"}
        dominantBaseline="central"
        fontWeight="bold"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 shadow-md rounded-md">
          <p className="font-medium text-gray-600">{payload[0].name}</p>
          <p className="text-gray-800">
            <span className="font-bold">{payload[0].value}</span> items
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <h2 className="text-lg font-medium mb-4 text-gray-700">
        Distribución por Status
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Folder Status Chart */}
        <div>
          <h3 className="text-base font-medium text-center mb-4">Folders</h3>
          <div className="flex flex-col md:flex-row">
            <div className="h-64 w-full">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={formattedFolderData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {formattedFolderData.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={FOLDER_COLORS[index % FOLDER_COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="flex justify-center mt-2 space-x-8">
            {formattedFolderData.map((item, index) => (
              <div key={index} className="flex items-center">
                <div
                  className="w-3 h-3 rounded-sm mr-2"
                  style={{
                    backgroundColor:
                      FOLDER_COLORS[index % FOLDER_COLORS.length],
                  }}
                ></div>
                <div>
                  <span className="text-sm font-medium">{item.name}:</span>
                  <span className="text-sm ml-1">{item.value}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Requirement Status Chart */}
        <div>
          <h3 className="text-base font-medium text-center mb-4">Requisitos</h3>
          <div className="flex flex-col md:flex-row">
            <div className="h-64 w-full">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={formattedRequirementData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomizedLabel}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {formattedRequirementData.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          REQUIREMENT_COLORS[index % REQUIREMENT_COLORS.length]
                        }
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="flex flex-wrap justify-center mt-2 gap-3">
            {formattedRequirementData.map((item, index) => (
              <div key={index} className="flex items-center">
                <div
                  className="w-3 h-3 rounded-sm mr-2"
                  style={{
                    backgroundColor:
                      REQUIREMENT_COLORS[index % REQUIREMENT_COLORS.length],
                  }}
                ></div>
                <div>
                  <span className="text-sm font-medium">{item.name}:</span>
                  <span className="text-sm ml-1">{item.value}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusDistributionCharts;
